import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { motion } from 'framer-motion';
import {
  Settings2, Home, Building2, Trees, Users, Tag, Wallet, Percent,
  RotateCcw, Plus, Trash2, Save, ShieldAlert, Check, Database, Sparkles, Loader2, AlertTriangle,
} from 'lucide-react';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';

const TABS = [
  { key: 'property_types',     label: 'Property Types',      icon: Home },
  { key: 'vendor_trades',      label: 'Vendor Trades',       icon: Users },
  { key: 'expense_categories', label: 'Expense Categories',  icon: Tag },
  { key: 'invoice_statuses',   label: 'Invoice Statuses',    icon: Wallet },
  { key: 'snag_severities',    label: 'Snag Severities',     icon: ShieldAlert },
  { key: 'communities',        label: 'Communities',         icon: Building2 },
  { key: 'finance_defaults',   label: 'Finance & Fees',      icon: Percent },
  { key: 'demo_data',          label: 'Demo Data',           icon: Database },
];

const ASSET_CLASSES = [
  { key: 'residential', label: 'Residential', icon: Home },
  { key: 'commercial',  label: 'Commercial',  icon: Building2 },
  { key: 'land',        label: 'Land / Plot', icon: Trees },
];

export default function AdminSettings() {
  const { user } = useAuth();
  const [tab, setTab] = useState('property_types');
  const [settings, setSettings] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const isAdmin = user && ['owner', 'md'].includes(user.role);

  useEffect(() => { load(); }, []);
  const load = async () => {
    const r = await api.get('/admin/settings');
    setSettings(r.data);
    setDirty(false);
  };

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const r = await api.put('/admin/settings', settings);
      setSettings(r.data);
      setDirty(false);
      toast.success('Settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const resetDefaults = async () => {
    if (!window.confirm('Reset all settings to Dubai/UAE defaults? Your custom values will be lost.')) return;
    setSaving(true);
    try {
      const r = await api.post('/admin/settings/reset-defaults');
      setSettings(r.data);
      setDirty(false);
      toast.success('Settings reset to defaults');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to reset');
    } finally { setSaving(false); }
  };

  if (!settings) return <div className="py-10 text-slate-500">Loading admin settings…</div>;

  return (
    <div className="py-6 max-w-[1300px] mx-auto" data-testid="admin-settings-page">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="overline flex items-center gap-2"><Settings2 size={12}/> Workspace administration</div>
          <h1 className="font-serif text-4xl">Admin settings</h1>
          <div className="text-sm text-slate-500 mt-1 max-w-2xl">
            Configure every dropdown and financial default used across Iris Realty. Changes apply to the entire workspace and everyone in it.
          </div>
        </div>
        <div className="flex gap-2">
          <a href="/app/admin/sync-runs" data-testid="admin-sync-runs-link"
             className="btn-ghost text-sm flex items-center gap-2">
            <RotateCcw size={13}/> View nightly runs
          </a>
          <button onClick={resetDefaults} disabled={saving || !isAdmin} className="btn-ghost text-sm flex items-center gap-2" data-testid="reset-defaults-btn">
            <RotateCcw size={13}/> Reset to defaults
          </button>
          <button onClick={save} disabled={!dirty || saving || !isAdmin} className="btn-primary text-sm flex items-center gap-2" data-testid="save-settings-btn">
            {saving ? 'Saving…' : dirty ? <><Save size={13}/> Save changes</> : <><Check size={13}/> Saved</>}
          </button>
        </div>
      </div>

      {!isAdmin && (
        <div className="mb-6 p-4 rounded-2xl bg-amber-50 border border-amber-200 text-sm text-amber-900" data-testid="admin-readonly-banner">
          <b>Read-only.</b> Only workspace owners and MDs can edit these settings. Ask your admin to grant access.
        </div>
      )}

      <div className="grid grid-cols-12 gap-6">
        {/* Tabs */}
        <div className="col-span-12 lg:col-span-3">
          <div className="glass p-2 space-y-1 sticky top-4">
            {TABS.map(t => {
              const Icon = t.icon;
              const active = tab === t.key;
              const count = Array.isArray(settings[t.key]) ? settings[t.key].length : null;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  data-testid={`admin-tab-${t.key}`}
                  className={`w-full text-left flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                    active ? 'bg-[#1A2530] text-white' : 'text-slate-700 hover:bg-slate-100/80'
                  }`}
                >
                  <span className="flex items-center gap-2"><Icon size={14}/> {t.label}</span>
                  {count != null && <span className={`text-xs ${active ? 'text-white/70' : 'text-slate-400'}`}>{count}</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Panel */}
        <div className="col-span-12 lg:col-span-9">
          <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.15 }}>
            {tab === 'property_types' && (
              <PropertyTypesEditor
                value={settings.property_types}
                onChange={(v) => { setSettings({ ...settings, property_types: v }); setDirty(true); }}
                readOnly={!isAdmin}
              />
            )}
            {tab === 'finance_defaults' && (
              <FinanceDefaultsEditor
                value={settings.finance_defaults}
                onChange={(v) => { setSettings({ ...settings, finance_defaults: v }); setDirty(true); }}
                readOnly={!isAdmin}
              />
            )}
            {['vendor_trades', 'expense_categories', 'invoice_statuses', 'snag_severities', 'communities'].includes(tab) && (
              <StringListEditor
                title={TABS.find(t => t.key === tab).label}
                value={settings[tab]}
                onChange={(v) => { setSettings({ ...settings, [tab]: v }); setDirty(true); }}
                readOnly={!isAdmin}
                testKey={tab}
              />
            )}
            {tab === 'demo_data' && (
              <DemoDataPanel isAdmin={isAdmin} />
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}

// ---------- Demo Data Panel ----------
function DemoDataPanel({ isAdmin }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(null); // 'seed' | 'clear' | null

  const load = async () => {
    try {
      const r = await api.get('/demo/status');
      setStatus(r.data);
    } catch (e) { toast.error('Failed to load demo status'); }
  };
  useEffect(() => { load(); }, []);

  const seed = async () => {
    if (!window.confirm('Seed heavy demo data? This will WIPE existing demo workspace and reload rich sample data (properties, BOQs, invoices, market trends, auctions AI, drawings, permits, snags, tasks). Takes ~10 seconds.')) return;
    setBusy('seed');
    try {
      const r = await api.post('/demo/seed');
      toast.success(`Seeded ${Object.values(r.data.seeded || {}).reduce((a,b) => a + (typeof b === 'number' ? b : 0), 0).toLocaleString()} records`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Seed failed');
    } finally { setBusy(null); }
  };

  const clear = async () => {
    if (!window.confirm('⚠️ Clear ALL demo data? This deletes every property, invoice, BOQ, etc. in the demo workspace. Live workspace is unaffected. This cannot be undone.')) return;
    setBusy('clear');
    try {
      const r = await api.post('/demo/clear');
      toast.success(`Cleared ${r.data.total_deleted?.toLocaleString() || 0} demo records`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Clear failed');
    } finally { setBusy(null); }
  };

  const rows = status?.counts ? Object.entries(status.counts).sort() : [];

  return (
    <div className="glass p-6" data-testid="demo-data-panel">
      <div className="flex items-start gap-4 mb-6">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#679286] to-[#4a6f66] grid place-items-center shrink-0">
          <Database className="text-white" size={22}/>
        </div>
        <div className="flex-1">
          <h2 className="font-serif text-2xl">Demo workspace data</h2>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Load a rich, fully-populated demo workspace so your team can explore every module — properties, BOQ compare, approvals workflow, market trends, auctions AI, drawings, permits — without touching your live data. Only affects the <span className="font-medium text-slate-700">demo</span> workspace.
          </p>
        </div>
      </div>

      <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 mb-6 flex gap-3">
        <AlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={16}/>
        <div className="text-xs text-amber-900 leading-relaxed">
          <b>Safe to run:</b> These actions <b>only touch the &quot;demo&quot; workspace</b>. Your live data (irisrealty.ai team &amp; deals) is never modified — even if you&apos;re currently viewing Live mode. Switch to Demo mode from the header toggle after seeding to see the sample data.
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <button onClick={seed} disabled={busy != null || !isAdmin}
          data-testid="seed-demo-btn"
          className="rounded-2xl bg-gradient-to-br from-[#1A2530] to-[#282828] text-white p-5 text-left hover:shadow-xl transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed">
          <div className="flex items-center gap-3 mb-3">
            {busy === 'seed' ? <Loader2 className="animate-spin" size={22}/> : <Sparkles size={22} className="text-[#9BC7B8]"/>}
            <div className="font-serif text-lg">Seed heavy demo data</div>
          </div>
          <div className="text-xs text-slate-300 leading-relaxed">
            Creates 8 properties, ~7 BOQs across 4 projects (with fraud flags), invoices in every approval state, drawings, DM/DCD permits, buyers, tasks, snags, expenses, 500 market transactions, 300 listings, and AI verdicts on 8 auctions.
          </div>
        </button>

        <button onClick={clear} disabled={busy != null || !isAdmin || !status?.seeded}
          data-testid="clear-demo-btn"
          className="rounded-2xl border-2 border-red-200 bg-red-50/50 text-red-900 p-5 text-left hover:bg-red-50 transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed">
          <div className="flex items-center gap-3 mb-3">
            {busy === 'clear' ? <Loader2 className="animate-spin" size={22}/> : <Trash2 size={22}/>}
            <div className="font-serif text-lg">Clear all demo data</div>
          </div>
          <div className="text-xs text-red-800 leading-relaxed">
            Removes every record in the <b>demo</b> workspace. Live workspace is untouched. Use before reseeding or when demo mode should be empty.
          </div>
        </button>
      </div>

      {/* Live counts */}
      <div className="overline mb-3 flex items-center justify-between">
        <span>Current demo workspace</span>
        {status && (
          <span className="text-slate-500 normal-case tracking-normal text-xs">
            Total records: <b className="text-slate-800">{(status.total_records || 0).toLocaleString()}</b>
          </span>
        )}
      </div>

      {!status?.seeded ? (
        <div className="rounded-2xl border border-dashed border-slate-300 p-8 text-center" data-testid="demo-empty">
          <Database className="mx-auto text-slate-300 mb-2" size={28}/>
          <div className="text-sm text-slate-500">No demo data yet. Click <b>Seed heavy demo data</b> above.</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="demo-counts-grid">
          {rows.map(([k, v]) => (
            <div key={k} className="rounded-xl bg-white/70 border border-white/60 p-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-400">{k.replace(/_/g, ' ')}</div>
              <div className="font-serif text-2xl mt-1">{v.toLocaleString()}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Property Types Editor ----------
function PropertyTypesEditor({ value, onChange, readOnly }) {
  const [draft, setDraft] = useState({ value: '', label: '', asset_class: 'residential' });
  const add = () => {
    if (!draft.value.trim() || !draft.label.trim()) return;
    if (value.some(v => v.value === draft.value.trim())) {
      toast.error(`Value "${draft.value}" already exists`);
      return;
    }
    onChange([...value, {
      value: draft.value.trim().toLowerCase().replace(/\s+/g, '_'),
      label: draft.label.trim(),
      asset_class: draft.asset_class,
    }]);
    setDraft({ value: '', label: '', asset_class: draft.asset_class });
  };
  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));

  return (
    <div className="glass p-6" data-testid="property-types-editor">
      <div className="overline mb-1">Category</div>
      <h2 className="font-serif text-2xl mb-4">Property Types</h2>
      <p className="text-sm text-slate-500 mb-5">These options appear in the <b>Add Property</b> modal and drive the Residential / Commercial / Land grouping on the pipeline page.</p>

      <div className="space-y-6">
        {ASSET_CLASSES.map(a => {
          const items = value.filter(v => v.asset_class === a.key);
          const Icon = a.icon;
          return (
            <div key={a.key}>
              <div className="flex items-center gap-2 mb-2 text-sm font-medium text-slate-700">
                <Icon size={14}/> {a.label} <span className="text-xs text-slate-400 font-normal">({items.length})</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {items.map((t, i) => {
                  const globalIdx = value.findIndex(x => x.value === t.value);
                  return (
                    <div key={t.value} className="flex items-center justify-between gap-2 p-3 rounded-xl bg-white/70 border border-slate-200/60" data-testid={`ptype-row-${t.value}`}>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-800 truncate">{t.label}</div>
                        <div className="text-xs text-slate-400 font-mono truncate">{t.value}</div>
                      </div>
                      {!readOnly && (
                        <button onClick={() => remove(globalIdx)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500" data-testid={`ptype-remove-${t.value}`}>
                          <Trash2 size={13}/>
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {!readOnly && (
        <div className="mt-6 pt-6 border-t border-slate-200/70">
          <div className="text-sm font-medium mb-2">Add new type</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
            <label className="text-xs">
              Label
              <input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                placeholder="e.g. Duplex"
                className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm"
                data-testid="new-ptype-label"/>
            </label>
            <label className="text-xs">
              Slug (code)
              <input value={draft.value} onChange={(e) => setDraft({ ...draft, value: e.target.value })}
                placeholder="duplex"
                className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm font-mono"
                data-testid="new-ptype-value"/>
            </label>
            <label className="text-xs">
              Asset class
              <select value={draft.asset_class} onChange={(e) => setDraft({ ...draft, asset_class: e.target.value })}
                className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm"
                data-testid="new-ptype-asset">
                {ASSET_CLASSES.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
              </select>
            </label>
            <button onClick={add} className="btn-primary text-sm flex items-center justify-center gap-2" data-testid="add-ptype-btn">
              <Plus size={13}/> Add
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Generic string-list editor ----------
function StringListEditor({ title, value, onChange, readOnly, testKey }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (value.includes(v)) { toast.error(`"${v}" already exists`); return; }
    onChange([...value, v]);
    setDraft('');
  };
  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));

  return (
    <div className="glass p-6" data-testid={`stringlist-editor-${testKey}`}>
      <div className="overline mb-1">Category</div>
      <h2 className="font-serif text-2xl mb-1">{title}</h2>
      <p className="text-sm text-slate-500 mb-5">Manage the options shown in dropdowns across the platform. {value.length} entries.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {value.map((v, i) => (
          <div key={v} className="flex items-center justify-between gap-2 p-3 rounded-xl bg-white/70 border border-slate-200/60" data-testid={`${testKey}-row-${i}`}>
            <span className="text-sm text-slate-800 truncate">{v}</span>
            {!readOnly && (
              <button onClick={() => remove(i)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500" data-testid={`${testKey}-remove-${i}`}>
                <Trash2 size={13}/>
              </button>
            )}
          </div>
        ))}
      </div>

      {!readOnly && (
        <div className="mt-6 pt-6 border-t border-slate-200/70">
          <div className="text-sm font-medium mb-2">Add new</div>
          <div className="flex gap-2">
            <input value={draft} onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
              placeholder="Type a new value…"
              className="flex-1 px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm"
              data-testid={`${testKey}-input`}/>
            <button onClick={add} className="btn-primary text-sm flex items-center gap-2" data-testid={`${testKey}-add-btn`}>
              <Plus size={13}/> Add
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Finance Defaults ----------
const FINANCE_FIELDS = [
  { section: 'Mortgage', fields: [
    { key: 'mortgage_rate_pct',      label: 'Mortgage interest rate',    unit: '% p.a.', help: 'Typical UAE resident mortgage rate — currently ~4.5% (2026).' },
    { key: 'ltv_resident_pct',       label: 'Max LTV — Resident',        unit: '%',      help: 'Central Bank of UAE: 75% for first residential unit under AED 5M, 65% above AED 5M.' },
    { key: 'ltv_non_resident_pct',   label: 'Max LTV — Non-resident',    unit: '%',      help: 'Central Bank of UAE: 60% for non-residents.' },
    { key: 'mortgage_tenor_years',   label: 'Mortgage tenor',            unit: 'years',  help: 'Standard 25 years, max 25 for salaried, 20 for self-employed.' },
  ]},
  { section: 'Purchase closing costs (Dubai)', fields: [
    { key: 'dld_transfer_fee_pct',   label: 'DLD transfer fee',          unit: '%',      help: 'Dubai Land Department transfer fee — currently 4% of purchase price.' },
    { key: 'dld_admin_aed',          label: 'DLD admin fee',             unit: 'AED',    help: 'Flat administrative fee — currently AED 4,200.' },
    { key: 'agent_fee_pct',          label: 'Agent commission',          unit: '%',      help: 'Broker commission — market standard 2% + VAT.' },
    { key: 'mortgage_registration_pct', label: 'Mortgage registration', unit: '%',      help: 'DLD mortgage registration — 0.25% of loan amount + AED 290.' },
    { key: 'trustee_fee_aed',        label: 'Trustee office fee',        unit: 'AED',    help: 'Property trustee office fee — flat AED 4,200 typical.' },
    { key: 'valuation_fee_aed',      label: 'Bank valuation fee',        unit: 'AED',    help: 'Bank valuation for mortgage — AED 2,500-3,500.' },
  ]},
  { section: 'Tax', fields: [
    { key: 'vat_pct_commercial',     label: 'VAT — Commercial',          unit: '%',      help: 'UAE VAT on commercial property is 5%.' },
    { key: 'vat_pct_residential',    label: 'VAT — Residential resale',  unit: '%',      help: 'Residential resale is VAT-exempt (0%).' },
  ]},
  { section: 'Holding periods (defaults)', fields: [
    { key: 'avg_refurb_months',      label: 'Average refurb duration',   unit: 'months', help: 'Baseline refurb time — used for holding-cost calculations.' },
    { key: 'avg_marketing_months',   label: 'Average marketing period',  unit: 'months', help: 'Time to sell after refurb — Dubai average 2-4 months.' },
  ]},
];

function FinanceDefaultsEditor({ value, onChange, readOnly }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="glass p-6" data-testid="finance-defaults-editor">
      <div className="overline mb-1">Category</div>
      <h2 className="font-serif text-2xl mb-1">Finance & Fees</h2>
      <p className="text-sm text-slate-500 mb-6">
        These defaults feed the <b>Deal Analyzer</b> financing analysis, ROI calculations and cash-on-cash returns. Pre-populated with UAE 2026 standards — edit for your specific bank offers or non-standard deals.
      </p>

      <div className="space-y-8">
        {FINANCE_FIELDS.map(section => (
          <div key={section.section}>
            <div className="font-serif text-lg text-slate-900 mb-3 pb-2 border-b border-slate-200/60">{section.section}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {section.fields.map(f => (
                <div key={f.key} className="space-y-1" data-testid={`fin-field-${f.key}`}>
                  <label className="text-xs font-medium text-slate-700 flex items-center justify-between">
                    <span>{f.label}</span>
                    <span className="text-slate-400 font-normal">{f.unit}</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={value[f.key] ?? ''}
                    onChange={(e) => set(f.key, parseFloat(e.target.value) || 0)}
                    disabled={readOnly}
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm disabled:bg-slate-50 disabled:text-slate-500"
                    data-testid={`fin-input-${f.key}`}
                  />
                  <div className="text-[11px] text-slate-400">{f.help}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
